import { useEffect, useRef } from 'react';
import { formatRelativeShort } from '@/lib/relative-time';
import { cn } from '@/lib/utils';
import type { Notification, NotificationKind } from '@leanopx/shared';

/**
 * NotifDropdown — topbar bell popover. Shows the most recent N
 * notifications (default 5) with an icon-by-kind, title + sub, and
 * timestamp. Click-outside closes; clicking a row dispatches via
 * `onPick(n)` (typically navigates to the source surface).
 *
 * Per the Notification schema in `@leanopx/shared`, kind ∈
 * { alert, agent, signal, todo, scorecard } drives the icon + tone.
 *
 * Strings are accepted as props with English defaults so callers can
 * route them through `t()` (or any future i18n layer) without the
 * primitive depending on `apps/web/src/lib/i18n.ts`. Matches the
 * pattern used by `<EmptyState>` and other primitives in the library.
 */

const KIND_ICON: Record<NotificationKind, string> = {
  alert: '▲',
  agent: '✧',
  signal: '✦',
  todo: '✓',
  // Scorecard auto-pings (PR #45 / issue #45) — measurable went red.
  // Glyph mirrors the "watch" pip used in the scorecard's red-row dot.
  scorecard: '●',
};

const KIND_TONE: Record<NotificationKind, string> = {
  alert: 'text-[var(--color-rose)]',
  agent: 'text-[var(--color-accent)]',
  signal: 'text-[var(--color-sage)]',
  todo: 'text-[var(--color-accent)]',
  // Red tone since the only event that produces a 'scorecard' row today
  // is the on/watch → off auto-ping.
  scorecard: 'text-[var(--color-rose)]',
};

export function NotifDropdown({
  items,
  onPick,
  onMarkAllRead,
  onSeeAll,
  onClose,
  maxItems = 5,
  dialogLabel = 'Notifications',
  titleLabel = 'Notifications',
  newSuffix = 'new',
  markAllReadLabel = 'Mark all read',
  emptyLabel = "You're caught up.",
  seeAllLabel = 'See all in Alerts →',
  className,
}: {
  items: Notification[];
  onPick?: (n: Notification) => void;
  onMarkAllRead?: () => void;
  onSeeAll?: () => void;
  onClose: () => void;
  /** Cap on rendered rows. Defaults to 5 (matches the topbar bell brief). */
  maxItems?: number;
  /** `aria-label` on the dialog wrapper. */
  dialogLabel?: string;
  /** Header heading text. */
  titleLabel?: string;
  /** Suffix appended after the unread count (e.g. "5 new"). */
  newSuffix?: string;
  /** "Mark all read" button label (omit by passing falsy `onMarkAllRead`). */
  markAllReadLabel?: string;
  /** Empty-state copy. */
  emptyLabel?: string;
  /** Footer "see all" CTA label. */
  seeAllLabel?: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    // Defer the listener install one tick so the click that opens the
    // dropdown doesn't immediately re-close it.
    const id = setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const visibleItems = items.slice(0, maxItems);
  const unreadCount = visibleItems.filter((n) => !n.read).length;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={dialogLabel}
      className={cn(
        'flex w-[360px] flex-col overflow-hidden rounded-[var(--r-3,12px)] border border-[var(--color-line)] bg-[var(--color-surface)] shadow-2xl',
        className,
      )}
    >
      <header className="flex items-center justify-between border-b border-[var(--color-line)] px-4 py-3">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--color-ink-muted)]">
          {titleLabel} · {unreadCount} {newSuffix}
        </span>
        {onMarkAllRead && (
          <button
            type="button"
            onClick={onMarkAllRead}
            className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-[var(--color-accent)] hover:text-[var(--color-accent-bright)]"
          >
            {markAllReadLabel}
          </button>
        )}
      </header>
      <ul className="m-0 flex max-h-[60vh] flex-col gap-0 overflow-y-auto p-0">
        {visibleItems.map((n) => (
          <li key={n.id} className="m-0 list-none">
            <button
              type="button"
              onClick={() => onPick?.(n)}
              className="flex w-full items-start gap-3 border-b border-[var(--color-line)] px-4 py-3 text-start transition-colors hover:bg-[var(--color-surface-2)] last:border-b-0"
            >
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center text-[12px]',
                  KIND_TONE[n.kind],
                )}
              >
                {KIND_ICON[n.kind]}
              </span>
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    'truncate text-[13px] font-medium',
                    n.read ? 'text-[var(--color-ink-secondary)]' : 'text-[var(--color-ink)]',
                  )}
                >
                  {n.title}
                </div>
                {n.body && (
                  <div className="mt-0.5 line-clamp-2 text-[11.5px] text-[var(--color-ink-muted)]">
                    {n.body}
                  </div>
                )}
              </div>
              <span className="ms-1 font-mono text-[10.5px] text-[var(--color-ink-muted)]">
                {formatRelativeShort(n.at)}
              </span>
            </button>
          </li>
        ))}
        {visibleItems.length === 0 && (
          <li className="m-0 list-none px-4 py-8 text-center text-[12.5px] text-[var(--color-ink-muted)]">
            {emptyLabel}
          </li>
        )}
      </ul>
      {onSeeAll && (
        <footer className="border-t border-[var(--color-line)] px-4 py-2.5 text-end">
          <button
            type="button"
            onClick={onSeeAll}
            className="font-mono text-[11px] uppercase tracking-[0.06em] text-[var(--color-accent)] hover:text-[var(--color-accent-bright)]"
          >
            {seeAllLabel}
          </button>
        </footer>
      )}
    </div>
  );
}
